import { useState, useEffect, useCallback } from 'react';
import { supabase, Job } from '../../lib/supabase';
import JobCard from './components/JobCard';
import JobPreview from './components/JobPreview';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function JobsPage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [searchWhat, setSearchWhat] = useState('');
  const [searchWhere, setSearchWhere] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    datePosted: '',
    workType: [] as string[],
    experience: [] as string[],
    salary: [] as string[],
    tags: [] as string[],
  });
  const [showFilterDropdown, setShowFilterDropdown] = useState<string | null>(null);

  const JOBS_PER_PAGE = 10;

  // Fetch jobs from Supabase
  useEffect(() => {
    fetchJobs();
    
    // Set up Realtime subscription for instant updates
    const channel = supabase
      .channel('jobs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'jobs',
        },
        (payload) => {
          console.log('Realtime update:', payload);
          
          if (payload.eventType === 'INSERT') {
            setJobs((current) => [payload.new as Job, ...current]);
          } else if (payload.eventType === 'UPDATE') {
            setJobs((current) =>
              current.map((job) =>
                job.id === payload.new.id ? (payload.new as Job) : job
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setJobs((current) =>
              current.filter((job) => job.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setJobs(data || []);
      setFilteredJobs(data || []);
      if (data && data.length > 0) {
        setSelectedJob(data[0]);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced server-side search with RPC function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (whatQuery: string, whereQuery: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          if (!whatQuery.trim() && !whereQuery.trim()) {
            setFilteredJobs(jobs);
            setSearching(false);
            setCurrentPage(1);
            return;
          }

          try {
            setSearching(true);
            const { data, error } = await supabase.rpc('search_jobs', {
              search_query: whatQuery,
            });

            if (error) {
              console.error('Search error:', error);
              // Fallback to client-side search
              let filtered = jobs.filter(
                (job) =>
                  job.title.toLowerCase().includes(whatQuery.toLowerCase()) ||
                  job.company.toLowerCase().includes(whatQuery.toLowerCase()) ||
                  job.description?.toLowerCase().includes(whatQuery.toLowerCase()) ||
                  job.tags.some((tag) =>
                    tag.toLowerCase().includes(whatQuery.toLowerCase())
                  )
              );

              // Apply location filter
              if (whereQuery.trim()) {
                filtered = filtered.filter((job) =>
                  job.location.toLowerCase().includes(whereQuery.toLowerCase())
                );
              }

              setFilteredJobs(filtered);
            } else {
              let filtered = data || [];
              
              // Apply location filter
              if (whereQuery.trim()) {
                filtered = filtered.filter((job: Job) =>
                  job.location.toLowerCase().includes(whereQuery.toLowerCase())
                );
              }
              
              setFilteredJobs(filtered);
            }
            setCurrentPage(1);
          } catch (error) {
            console.error('Search error:', error);
          } finally {
            setSearching(false);
          }
        }, 300);
      };
    })(),
    [jobs]
  );

  // Handle search input change
  const handleSearch = () => {
    debouncedSearch(searchWhat, searchWhere);
  };

  // Apply filters
  useEffect(() => {
    let filtered = jobs;

    // Apply date posted filter
    if (filters.datePosted) {
      const now = new Date();
      const cutoffDate = new Date();
      
      if (filters.datePosted === '24h') {
        cutoffDate.setHours(now.getHours() - 24);
      } else if (filters.datePosted === '3days') {
        cutoffDate.setDate(now.getDate() - 3);
      } else if (filters.datePosted === '7days') {
        cutoffDate.setDate(now.getDate() - 7);
      }
      
      filtered = filtered.filter((job) => new Date(job.created_at) >= cutoffDate);
    }

    if (filters.workType.length > 0) {
      filtered = filtered.filter((job) =>
        filters.workType.includes(job.job_type)
      );
    }

    if (filters.experience.length > 0) {
      filtered = filtered.filter((job) =>
        filters.experience.includes(job.experience_level)
      );
    }

    if (filters.tags.length > 0) {
      filtered = filtered.filter((job) =>
        job.tags.some((tag) => filters.tags.includes(tag))
      );
    }

    if (filters.salary.length > 0) {
      filtered = filtered.filter((job) => {
        const salaryMatch = job.salary_range.match(/\$(\d+)k?\s*-\s*\$(\d+)k?/i);
        if (!salaryMatch) return false;
        
        const minSalary = parseInt(salaryMatch[1]) * (salaryMatch[1].length <= 3 ? 1000 : 1);
        
        return filters.salary.some((range) => {
          if (range === '40k-80k') return minSalary >= 40000 && minSalary <= 80000;
          if (range === '80k-120k') return minSalary >= 80000 && minSalary <= 120000;
          if (range === '120k+') return minSalary >= 120000;
          return false;
        });
      });
    }

    setFilteredJobs(filtered);
    setCurrentPage(1);
    
    if (filtered.length > 0 && !filtered.find((j) => j.id === selectedJob?.id)) {
      setSelectedJob(filtered[0]);
    }
  }, [filters, jobs]);

  const handleResetFilters = () => {
    setSearchWhat('');
    setSearchWhere('');
    setFilters({
      datePosted: '',
      workType: [],
      experience: [],
      salary: [],
      tags: [],
    });
    setFilteredJobs(jobs);
    setCurrentPage(1);
  };

  const toggleFilter = (filterType: string, value: string) => {
    setFilters((prev) => {
      const currentValues = prev[filterType as keyof typeof prev];
      
      if (filterType === 'datePosted') {
        return { ...prev, datePosted: prev.datePosted === value ? '' : value };
      }
      
      if (Array.isArray(currentValues)) {
        const newValues = currentValues.includes(value)
          ? currentValues.filter((v) => v !== value)
          : [...currentValues, value];
        return { ...prev, [filterType]: newValues };
      }
      
      return prev;
    });
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);
  const startIndex = (currentPage - 1) * JOBS_PER_PAGE;
  const endIndex = startIndex + JOBS_PER_PAGE;
  const currentJobs = filteredJobs.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Global Navigation Bar */}
      <Navbar />

      {/* Search Hero */}
      <div className="bg-[#1c1c1c] shadow-lg border-b border-[#333333] pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-semibold text-white mb-6">Find Your Next Job</h1>
          
          {/* Search Inputs */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-xl text-[#a0a0a0]"></i>
              <input
                type="text"
                value={searchWhat}
                onChange={(e) => setSearchWhat(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Job title, keywords, or company"
                className="w-full pl-12 pr-4 py-3 bg-[#2d2d2d] border border-[#333333] rounded text-white placeholder-[#a0a0a0] focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]"
              />
            </div>
            
            <div className="flex-1 relative">
              <i className="ri-map-pin-line absolute left-4 top-1/2 -translate-y-1/2 text-xl text-[#a0a0a0]"></i>
              <input
                type="text"
                value={searchWhere}
                onChange={(e) => setSearchWhere(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="City, state, or zip code"
                className="w-full pl-12 pr-4 py-3 bg-[#2d2d2d] border border-[#333333] rounded text-white placeholder-[#a0a0a0] focus:outline-none focus:border-[#6366f1] focus:ring-1 focus:ring-[#6366f1]"
              />
            </div>
            
            <button
              onClick={handleSearch}
              disabled={searching}
              className="px-8 py-3 bg-[#6366f1] text-white font-semibold rounded hover:bg-[#5558e3] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {searching ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Searching...
                </span>
              ) : (
                'Find Jobs'
              )}
            </button>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-[#a0a0a0]">
            <strong className="text-white">{filteredJobs.length}</strong> jobs found
          </div>
        </div>
      </div>

      {/* Filter Bar - FIXED: Now relative (static) instead of sticky */}
      <div className="bg-[#1c1c1c] border-b border-[#333333] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 custom-scrollbar">
            {/* Date Posted Filter */}
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(showFilterDropdown === 'date' ? null : 'date')}
                className={`px-4 py-2 border rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                  filters.datePosted
                    ? 'bg-[#6366f1] text-white border-[#6366f1]'
                    : 'bg-[#2d2d2d] text-white border-[#333333] hover:border-[#6366f1]'
                }`}
              >
                <i className="ri-calendar-line mr-2"></i>
                Date Posted
                <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
              
              {showFilterDropdown === 'date' && (
                <div className="absolute top-full left-0 mt-2 bg-[#1c1c1c] border border-[#333333] rounded shadow-lg z-20 min-w-[200px]">
                  {['24h', '3days', '7days'].map((period) => (
                    <button
                      key={period}
                      onClick={() => {
                        toggleFilter('datePosted', period);
                        setShowFilterDropdown(null);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-[#2d2d2d] cursor-pointer ${
                        filters.datePosted === period ? 'bg-[#2d2d2d] text-[#6366f1]' : 'text-white'
                      }`}
                    >
                      {period === '24h' ? 'Last 24 hours' : period === '3days' ? 'Last 3 days' : 'Last 7 days'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Work Type Filter */}
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(showFilterDropdown === 'workType' ? null : 'workType')}
                className={`px-4 py-2 border rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                  filters.workType.length > 0
                    ? 'bg-[#6366f1] text-white border-[#6366f1]'
                    : 'bg-[#2d2d2d] text-white border-[#333333] hover:border-[#6366f1]'
                }`}
              >
                <i className="ri-home-office-line mr-2"></i>
                Remote/Hybrid
                {filters.workType.length > 0 && ` (${filters.workType.length})`}
                <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
              
              {showFilterDropdown === 'workType' && (
                <div className="absolute top-full left-0 mt-2 bg-[#1c1c1c] border border-[#333333] rounded shadow-lg z-20 min-w-[200px]">
                  {['Remote', 'Hybrid', 'On-site'].map((type) => (
                    <label
                      key={type}
                      className="flex items-center px-4 py-2 hover:bg-[#2d2d2d] cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filters.workType.includes(type)}
                        onChange={() => toggleFilter('workType', type)}
                        className="mr-3 w-4 h-4 text-[#6366f1] bg-[#2d2d2d] border-[#333333] rounded focus:ring-[#6366f1] cursor-pointer"
                      />
                      <span className="text-white">{type}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Salary Filter */}
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(showFilterDropdown === 'salary' ? null : 'salary')}
                className={`px-4 py-2 border rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                  filters.salary.length > 0
                    ? 'bg-[#6366f1] text-white border-[#6366f1]'
                    : 'bg-[#2d2d2d] text-white border-[#333333] hover:border-[#6366f1]'
                }`}
              >
                <i className="ri-money-dollar-circle-line mr-2"></i>
                Salary
                {filters.salary.length > 0 && ` (${filters.salary.length})`}
                <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
              
              {showFilterDropdown === 'salary' && (
                <div className="absolute top-full left-0 mt-2 bg-[#1c1c1c] border border-[#333333] rounded shadow-lg z-20 min-w-[200px]">
                  {['40k-80k', '80k-120k', '120k+'].map((range) => (
                    <label
                      key={range}
                      className="flex items-center px-4 py-2 hover:bg-[#2d2d2d] cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filters.salary.includes(range)}
                        onChange={() => toggleFilter('salary', range)}
                        className="mr-3 w-4 h-4 text-[#6366f1] bg-[#2d2d2d] border-[#333333] rounded focus:ring-[#6366f1] cursor-pointer"
                      />
                      <span className="text-white">
                        {range === '120k+' ? '$120k+' : `$${range.replace('k-', 'k - $')}`}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Experience Filter */}
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(showFilterDropdown === 'experience' ? null : 'experience')}
                className={`px-4 py-2 border rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                  filters.experience.length > 0
                    ? 'bg-[#6366f1] text-white border-[#6366f1]'
                    : 'bg-[#2d2d2d] text-white border-[#333333] hover:border-[#6366f1]'
                }`}
              >
                <i className="ri-briefcase-line mr-2"></i>
                Experience
                {filters.experience.length > 0 && ` (${filters.experience.length})`}
                <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
              
              {showFilterDropdown === 'experience' && (
                <div className="absolute top-full left-0 mt-2 bg-[#1c1c1c] border border-[#333333] rounded shadow-lg z-20 min-w-[200px]">
                  {['Entry Level', '1-3 years', '3-5 years', '5+ years', '7+ years'].map((level) => (
                    <label
                      key={level}
                      className="flex items-center px-4 py-2 hover:bg-[#2d2d2d] cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filters.experience.includes(level)}
                        onChange={() => toggleFilter('experience', level)}
                        className="mr-3 w-4 h-4 text-[#6366f1] bg-[#2d2d2d] border-[#333333] rounded focus:ring-[#6366f1] cursor-pointer"
                      />
                      <span className="text-white">{level}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Reset Filters */}
            {(filters.datePosted || filters.workType.length > 0 || filters.salary.length > 0 || filters.experience.length > 0) && (
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 text-[#6366f1] hover:text-[#5558e3] whitespace-nowrap cursor-pointer font-medium"
              >
                <i className="ri-close-circle-line mr-1"></i>
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Job List - Left Column */}
          <div className="lg:col-span-5">
            <div className="space-y-4">
              {loading ? (
                // Loading Skeletons
                <>
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-[#1c1c1c] border border-[#333333] rounded p-6 animate-shimmer h-48"></div>
                  ))}
                </>
              ) : filteredJobs.length === 0 ? (
                <div className="text-center py-12 bg-[#1c1c1c] rounded border border-[#333333]">
                  <div className="w-24 h-24 mx-auto mb-6 bg-[#2d2d2d] rounded-full flex items-center justify-center">
                    <i className="ri-inbox-line text-4xl text-[#a0a0a0]"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">No Jobs Found</h3>
                  <p className="text-[#a0a0a0] mb-6">Try adjusting your search or filters</p>
                  <button
                    onClick={handleResetFilters}
                    className="px-6 py-3 bg-[#6366f1] text-white font-semibold rounded hover:bg-[#5558e3] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    Reset Filters
                  </button>
                </div>
              ) : (
                <>
                  {currentJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      isSelected={selectedJob?.id === job.id}
                      onClick={() => setSelectedJob(job)}
                    />
                  ))}
                  
                  {/* Pagination Component */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 py-8">
                      {/* Previous Button */}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          currentPage === 1
                            ? 'bg-[#2d2d2d] text-[#666666] cursor-not-allowed'
                            : 'bg-[#2d2d2d] text-white hover:bg-[#6366f1] border border-[#333333]'
                        }`}
                      >
                        <i className="ri-arrow-left-s-line mr-1"></i>
                        Prev
                      </button>

                      {/* Page Numbers */}
                      {renderPageNumbers().map((page, index) => (
                        <button
                          key={index}
                          onClick={() => typeof page === 'number' && handlePageChange(page)}
                          disabled={page === '...'}
                          className={`w-10 h-10 rounded font-medium transition-colors cursor-pointer ${
                            page === currentPage
                              ? 'bg-[#6366f1] text-white'
                              : page === '...'
                              ? 'bg-transparent text-[#a0a0a0] cursor-default'
                              : 'bg-[#2d2d2d] text-white hover:bg-[#6366f1] border border-[#333333]'
                          }`}
                        >
                          {page}
                        </button>
                      ))}

                      {/* Next Button */}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          currentPage === totalPages
                            ? 'bg-[#2d2d2d] text-[#666666] cursor-not-allowed'
                            : 'bg-[#2d2d2d] text-white hover:bg-[#6366f1] border border-[#333333]'
                        }`}
                      >
                        Next
                        <i className="ri-arrow-right-s-line ml-1"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Job Preview - Right Column (Sticky) */}
          <div className="lg:col-span-7 hidden lg:block">
            <div className="sticky top-32">
              {selectedJob ? (
                <JobPreview job={selectedJob} />
              ) : (
                <div className="bg-[#1c1c1c] border border-[#333333] rounded p-12 text-center">
                  <i className="ri-file-list-3-line text-6xl text-[#a0a0a0] mb-4"></i>
                  <p className="text-[#a0a0a0]">Select a job to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {showFilterDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowFilterDropdown(null)}
        ></div>
      )}

      {/* Global Footer */}
      <Footer />
    </div>
  );
}
